/**
 * Module valuation-leasehold defines leasehold valuation parameters, ground rent,
 * compensation, deductions, and market and mortgage-lending results.
 *
 * Economic-unit composition is defined in valuation-details.ts.
 */
import { z } from 'zod';
import { ISODateSchema } from './common';
import { PercentageSchema } from './measurements';
import { MonetaryValueSchema } from './money';
import { RoundingStrategySchema } from './valuation-primitives';
const LeaseholdValuationSchema = z.object({
    chosen_valuation_method_value: MonetaryValueSchema,
    full_leasehold_deduction: MonetaryValueSchema,
    leasehold_value_non_rounded: MonetaryValueSchema,
    leasehold_value_rounded: MonetaryValueSchema,
});
export const CombinedLeaseholdValuationSchema = z.object({
    market_value: LeaseholdValuationSchema,
    mortgage_value: LeaseholdValuationSchema,
});
const DiscountedLandValueParameterSchema = z.object({
    interest_rate: PercentageSchema.optional(),
    note: z.string().optional(),
});
const StructureCompensationParameterSchema = z.object({
    value_to_deduct: MonetaryValueSchema,
    note: z.string().optional(),
});
const GeneralDisadvantageScopeSchema = z.enum(['market_value', 'mortgage_lending_value', 'land_value']);
const GeneralDisadvantageParameterSchema = z.object({
    scope: GeneralDisadvantageScopeSchema,
    disadvantage: PercentageSchema,
    note: z.string().optional(),
});
const EffectiveGroundRentParameterSchema = z.object({
    ground_rent: MonetaryValueSchema,
    capitalization_interest_rate: PercentageSchema.optional(),
    note: z.string().optional(),
});
const LeaseholdParameterSchema = z.object({
    discounted_land_value: DiscountedLandValueParameterSchema.optional(),
    structure_compensation: StructureCompensationParameterSchema.optional(),
    general_disadvantage: GeneralDisadvantageParameterSchema.optional(),
    effective_ground_rent: EffectiveGroundRentParameterSchema.optional(),
    rounding_strategy: RoundingStrategySchema.optional(),
    user_defined_value: MonetaryValueSchema.optional(),
});
export const LeaseholdSchema = z.object({
    contract_start_date: ISODateSchema.optional(),
    contract_end_date: ISODateSchema.optional(),
    market_value_parameters: LeaseholdParameterSchema.optional(),
    mortgage_value_parameters: LeaseholdParameterSchema.optional(),
});
const LeaseholdDeductionSchema = z.object({
    residual_contract_duration: z.number().optional(),
    discounted_land_value: MonetaryValueSchema,
    structure_compensation: MonetaryValueSchema,
    general_disadvantage: MonetaryValueSchema,
    effective_ground_rent: MonetaryValueSchema,
    full_deduction: MonetaryValueSchema,
});
export const LeaseholdResultsSchema = z.object({
    market_value_deductions: LeaseholdDeductionSchema,
    mortgage_value_deductions: LeaseholdDeductionSchema,
});
